/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import { FocusStrategy } from '@react-types/shared';
import { useFormValidationState } from '@react-stately/form';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useListState } from '@react-stately/list';
import { useState } from 'react';
import { MultiSelectState, MultiSelectStateOptions } from './MultiSelect.types';

/**
 * Provides state management for a select component. Handles building a collection
 * of items from props, handles the open state for the popup menu, and manages
 * multiple selection state.
 */
export function useMultiSelectState<T extends object>(
  props: MultiSelectStateOptions<T>,
): MultiSelectState<T> {
  const triggerState = useOverlayTriggerState(props);
  const [focusStrategy, setFocusStrategy] = useState<FocusStrategy>('first');
  const listState = useListState({
    ...props,
    selectionMode: 'multiple',
    onSelectionChange(keys) {
      if (props.onSelectionChange != null) {
        props.onSelectionChange(keys);
      }

      validationState.commitValidation();
    },
  });

  const validationState = useFormValidationState({
    ...props,
    value: listState.selectionManager.selectedKeys,
  });

  const [isFocused, setFocused] = useState(false);

  return {
    ...validationState,
    ...listState,
    ...triggerState,
    focusStrategy,
    open(focusStrategy: FocusStrategy = 'first') {
      // Don't open if the collection is empty.
      if (listState.collection.size !== 0) {
        setFocusStrategy(focusStrategy);
        triggerState.open();
      }
    },
    toggle(focusStrategy: FocusStrategy = 'first') {
      if (listState.collection.size !== 0) {
        setFocusStrategy(focusStrategy);
        triggerState.toggle();
      }
    },
    isFocused,
    setFocused,
  };
}
